import React, { useEffect, useState } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Button,
  IconButton,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { buttonMarginSpace, buttonWidth } from '../../constants/form';
import { useFormik } from 'formik';
import { invoiceSchema } from './schemas/indexSchema';
import { client } from '../../../../util';
import type {
  Invoice,
  InvoiceCreateInput,
  PaymentTerms,
  ShipMethods,
  TaxCode,
} from '@repo/api-contract';
import { pushToast, useDispatch, useSelector } from '../../../../state';
import {
  closeModal,
  fetchInvoiceToEdit,
  InvoiceModalMode,
  setFormData,
  setLoading,
  setMode,
} from '../../../../state/invoiceModalSlice';
import DraggablePaper from '../../DraggablePaper';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import { serverError } from '../../constants/errors';
import { serverLatencyMessage } from '../../constants/messages';
import InvoiceDetails from './InvoiceDetails';
import Items from './Items';
import Customer from './Customer';
import SaleMarginAndCommission from './SaleMarginAndCommission';
import { getFormikField } from '../../../../util/fornik-helpers';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function CreateEditInvoiceModal() {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tAddI } = useTranslation([], {
    keyPrefix: 'team.Modals.Invoices.createAndEdit.Add Invoice',
  });

  const {
    mode,
    stateIsChanged,
    formData,
    isOpenInvoiceModal,
    invoiceIdToEdit,
    updateCallback,
    createCallback,
    loading,
  } = useSelector(({ invoiceModalSlice }) => invoiceModalSlice);
  const { team } = useSelector(({ userSlice }) => userSlice);

  const [tab, setTab] = useState(0);

  const dispatch = useDispatch();

  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms[]>([]);
  const [shipMethods, setShipMethods] = useState<ShipMethods[]>([]);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);

  const updateLoading = (value: boolean) => {
    dispatch(setLoading(value));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const resetState = () => {
    setTab(0);
    formik.resetForm();
  };

  const handleClose = () => {
    dispatch(closeModal({ resetState: true }));
    resetState();
  };

  const exitViewMode = async () => {
    dispatch(setMode(InvoiceModalMode.EDIT));
  };

  const getSuggestedCode = async () => {
    const code = await client.getSuggestedInvoiceCode.query();
    formik.setFieldValue('code', code);
  };
  const checkInvoiceCode = async (code: string) => {
    const number = Number(code);
    const resp = await client.getCounters.query();
    if (number < resp.invoiceNumber) {
      formik.setFieldError(
        'code',
        `Must be more than ${resp.invoiceNumber - 1}`,
      );
      return false;
    }
    return true;
  };

  const handleSave = async (payload: InvoiceCreateInput) => {
    updateLoading(true);
    if (mode == InvoiceModalMode.EDIT) {
      // check if modal is in edit mode
      if (invoiceIdToEdit) {
        // double check if id exists. Should never happen.
        try {
          const updatedInvoice = await client.updateInvoice.mutate({
            ...payload,
            code: payload.code.toString(),
            monthlyServiceCharge: payload.monthlyServiceCharge / 100,
            InvoiceId: invoiceIdToEdit,
          });
          if (updateCallback) updateCallback(updatedInvoice);
          dispatch(
            pushToast({
              message: `Invoice ${updatedInvoice?.code || ''} updated`,
              additionalInfo: serverLatencyMessage,
              type: 'success',
              duration: 7000,
            }),
          );
        } catch (error) {
          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
          updateLoading(false);
          return;
        }
      } else {
        // this should never happaned but its good to have this
        console.log('ERROR: Invoice id is wrong or missing: ', invoiceIdToEdit);
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    } else {
      const isValid = await checkInvoiceCode(payload.code);
      if (!isValid) {
        updateLoading(false);
        setTab(0);
        return;
      }
      try {
        if (team === 'init' || team === 'loading') return;
        const newInvoice: Invoice = await client.createInvoice.mutate({
          ...payload,
          code: payload.code.toString(),
          monthlyServiceCharge: payload.monthlyServiceCharge / 100,
        });
        if (createCallback) createCallback(newInvoice);
        dispatch(
          pushToast({
            message: `Invoice ${newInvoice?.code || ''} created`,
            additionalInfo: serverLatencyMessage,
            type: 'success',
            duration: 7000,
          }),
        );
      } catch (error) {
        if (error instanceof TRPCClientError) {
          dispatch(pushToast({ message: error.message, type: 'error' }));
        } else {
          dispatch(pushToast({ message: serverError, type: 'error' }));
        }
        updateLoading(false);
        return;
      }
    }
    handleClose();
    updateLoading(false);
  };

  // Use formData from redux as initialValues
  const formik = useFormik({
    initialValues: formData,
    enableReinitialize: false, // Do not Reinitialize form values when formData in redux changes
    validationSchema: invoiceSchema,
    onSubmit: handleSave,
  });

  // Sync formik values back to Redux when they change
  useEffect(() => {
    dispatch(setFormData(formik.values));
  }, [formik.values]);

  const fetchOptions = async () => {
    try {
      const [taxCodes, shipMethods, paymentTerms] = await Promise.all([
        client.allTaxCodes.query(),
        client.allShippingMethods.query(),
        client.allPaymentTerms.query(),
      ]);
      setTaxCodes(taxCodes.results);
      setShipMethods(shipMethods.results);
      setPaymentTerms(paymentTerms.results);
    } catch (error) {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
      handleClose();
    }
  };

  // After fetching invoice when editing:
  useEffect(() => {
    const fetchData = async () => {
      if (invoiceIdToEdit) {
        fetchOptions();
        try {
          const invoice = await dispatch(fetchInvoiceToEdit(invoiceIdToEdit));
          // Once we have the invoice, set it into the formData
          formik.setValues({
            ...formik.values, // Keeping any other existing form values
            ...invoice, // Seting vendor data returned from API
            monthlyServiceCharge: invoice.monthlyServiceCharge * 100,
          });
        } catch (error) {
          handleClose();

          if (error instanceof TRPCClientError) {
            dispatch(pushToast({ message: error.message, type: 'error' }));
          } else {
            dispatch(pushToast({ message: serverError, type: 'error' }));
          }
        }
      }
    };
    fetchData();
  }, [invoiceIdToEdit, dispatch]);

  useEffect(() => {
    // Here we don't make api calls if modal sate not changed (came back from other modal)
    // We also need to chek if its in edit mode because the state will be changed in it but we still need to fetch
    if (!isOpenInvoiceModal) return;
    if (stateIsChanged) return;
    const fetchData = async () => {
      updateLoading(true);
      await Promise.all([fetchOptions(), getSuggestedCode()]);
      updateLoading(false);
    };
    // Getting Initial data from formData (redux)
    formik.setValues(formData);
    fetchData();
  }, [isOpenInvoiceModal]);

  const renderTitle = () => {
    switch (mode) {
      case InvoiceModalMode.EDIT:
        return tAddI('Edit Invoice');
      case InvoiceModalMode.VIEW:
        return tAddI('View Invoice');
      case InvoiceModalMode.CREATE:
        return tAddI('Add Invoice');
      default:
        return '';
    }
  };

  // This functions set default values
  useEffect(() => {
    if (paymentTerms.length > 0) {
      const defaultTerm = paymentTerms.find((pT) => pT.default);
      if (defaultTerm) {
        formik.setFieldValue('paymentTerms', [defaultTerm]);
      }
    }
    if (shipMethods.length > 0) {
      const defaultCode = shipMethods.find((sM) => sM.default);
      if (defaultCode) {
        formik.setFieldValue('shipMethod', defaultCode.ShipMethodId);
      }
    }
  }, [paymentTerms, shipMethods]); // Only run when `paymentTerms` or `shipMethods` changes

  return (
    <Dialog
      open={isOpenInvoiceModal}
      onClose={(event, reason) => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth={tab == 1 ? 'xl' : 'lg'}
      scroll="paper"
      PaperProps={{ className: 'w-full' }}
      PaperComponent={DraggablePaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ m: 0, p: 1, cursor: 'move' }}
      >
        {renderTitle() + ` #` + getFormikField(formik, 'code').value}
      </DialogTitle>
      <IconButton
        disabled={loading}
        color="primary"
        sx={{ position: 'absolute', right: 4, top: 4 }}
        onClick={handleClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ p: 1 }}>
        {loading ? (
          <Box
            height="400px"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress size={80} />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Container>
              <Tabs
                centered
                value={tab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-around',
                  },
                }}
              >
                <Tab
                  sx={{ fontWeight: 'bold', width: '25%' }}
                  label={tAddI('INVOICE DETAILS')}
                  {...a11yProps(0)}
                />
                <Tab
                  sx={{ fontWeight: 'bold', width: '25%' }}
                  label={tAddI('ADD ITEM')}
                  {...a11yProps(1)}
                />
                <Tab
                  sx={{ fontWeight: 'bold', width: '25%' }}
                  label={tAddI('CUSTOMER')}
                  {...a11yProps(2)}
                />
                <Tab
                  sx={{ fontWeight: 'bold', width: '25%' }}
                  label={tAddI('SALE MARGIN & COMMISSION')}
                  {...a11yProps(3)}
                />
              </Tabs>
            </Container>
            <TabPanel value={tab} index={0}>
              <InvoiceDetails
                formik={formik}
                paymentTerms={paymentTerms}
                shipMethods={shipMethods}
                viewMode={mode == InvoiceModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={1}>
              <Items formik={formik} viewMode={mode == InvoiceModalMode.VIEW} />
            </TabPanel>
            <TabPanel value={tab} index={2}>
              <Customer
                formik={formik}
                taxCodes={taxCodes}
                viewMode={mode == InvoiceModalMode.VIEW}
              />
            </TabPanel>
            <TabPanel value={tab} index={3}>
              <SaleMarginAndCommission
                formik={formik}
                viewMode={mode == InvoiceModalMode.VIEW}
              />
            </TabPanel>
          </form>
        )}
      </DialogContent>
      <DialogActions>
        <Box width="100%" sx={{ display: 'flex', justifyContent: 'end' }}>
          <Button
            disabled={loading}
            variant="outlined"
            color="primary"
            sx={{ width: buttonWidth, marginRight: buttonMarginSpace }}
            onClick={handleClose}
          >
            {tGlobal('Cancel')}
          </Button>
          {mode == InvoiceModalMode.VIEW ? (
            <Button
              disabled={loading}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                exitViewMode();
              }}
            >
              {loading ? <CircularProgress size={20} /> : tGlobal('Edit')}
            </Button>
          ) : (
            <Button
              disabled={loading}
              variant="contained"
              color="primary"
              sx={{ width: buttonWidth }}
              onClick={() => {
                formik.submitForm();
              }}
            >
              {loading ? <CircularProgress size={20} /> : tGlobal('Save')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
